import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { getPayroll, PayrollResp, money } from '../finances/api';
import { teacherPhotoUrl } from '../teachers/api';
import { Skeleton } from '../../components/Skeleton';
import { readCache, writeCache } from '../../services/localCache';

/** Widget de HOME: nómina de maestros de la SEMANA (por HORARIO; comisión = mes). Karla lo pidió en el inicio. */
export default function PayrollWeekPanel() {
  // Pinta al instante lo último conocido y refresca por detrás (el fetch corrige
  // el rango si cambió de semana).
  const [resp, setResp] = useState<PayrollResp | null | 'err'>(() => readCache<PayrollResp>('payroll-week') ?? null);
  // Importes OCULTOS por defecto (Carlos 29-sep-2026): el Inicio se ve en el mostrador; lo que gana cada
  // maestro solo aparece al tocar el ojito, y se vuelve a tapar al salir o recargar.
  const [ver, setVer] = useState(false);
  const importe = (n: number) => (ver ? money(n) : '$ ****');

  useEffect(() => {
    getPayroll('week')
      .then((r) => { setResp(r); writeCache('payroll-week', r); })
      .catch(() => setResp((prev) => (prev && prev !== 'err' ? prev : 'err')));
  }, []);

  if (resp === 'err') return null;
  const labelRange = resp
    // Etiqueta lunes–sábado (Karla): el sábado es from+5.
    ? `${new Date(resp.from + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${new Date(new Date(resp.from + 'T12:00:00').getTime() + 5 * 86400000).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
    : '';

  return (
    <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          <Wallet size={16} className="text-accent-ink" /> Nómina de la semana
          <button type="button" onClick={() => setVer((v) => !v)} data-testid="nomina-ojito"
            title={ver ? 'Ocultar importes' : 'Ver importes'} aria-label={ver ? 'Ocultar importes' : 'Ver importes'} aria-pressed={ver}
            className="rounded-md p-1 text-content-3 transition hover:bg-surface-2 hover:text-content">
            {ver ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </h3>
        <Link to="/finanzas" className="text-xs font-medium text-brand-blue hover:underline">Ver nómina →</Link>
      </div>

      {resp === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : resp.teachers.length === 0 ? (
        // El backend solo cuenta clases CON maestro asignado (teacher_leader_id): decir
        // "sin clases" afirmaba de más — puede haber listas tomadas en clases sin titular.
        <p className="py-6 text-center text-sm text-content-3">Sin clases con maestro asignado esta semana.</p>
      ) : (
        <>
          <div className="mb-1 flex items-baseline gap-2">
            <button type="button" onClick={() => setVer((v) => !v)} data-testid="nomina-total" title={ver ? 'Ocultar importes' : 'Ver importes'}
              className="font-display text-2xl font-bold tabular-nums text-red-600 dark:text-red-400">{importe(resp.totals.pay)}</button>
            {/* Sin la marca de parcial, el total se lee como la nómina completa cuando en
                realidad solo suma a los maestros que YA tienen tarifa cargada. */}
            {resp.totals.missing_rates > 0 && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">parcial</span>
            )}
            <span className="text-xs text-content-3">{labelRange} · {resp.totals.hours} h</span>
          </div>
          {resp.totals.missing_rates > 0 && (
            <Link to="/finanzas" className="mb-2 flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:underline">
              <AlertTriangle size={12} /> Faltan {resp.totals.missing_rates} de {resp.totals.teachers} tarifas — sus horas no suman al total
            </Link>
          )}
          <ul className="-mr-1 mt-2 max-h-56 space-y-1 overflow-y-auto pb-3 pr-1 [scrollbar-width:thin]">
            {resp.teachers.slice(0, 6).map((t) => (
              <li key={t.teacher_id} className="flex items-center gap-2.5 rounded-lg px-1 py-1">
                {t.has_photo
                  ? <img src={teacherPhotoUrl(t.teacher_id)} alt="" className="h-8 w-8 rounded-full object-cover" />
                  : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent-ink">{t.teacher_name?.[0]}</div>}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-content">{t.teacher_name}</span>
                <span className="shrink-0 text-xs text-content-3">{t.pay_mode === 'commission' ? `${t.commission_pct}% mes` : `${t.total_hours} h`}</span>
                {t.has_rate
                  ? <span className="w-20 shrink-0 text-right text-sm font-semibold tabular-nums text-content">{importe(t.pay!)}</span>
                  : <span className="w-20 shrink-0 text-right text-[11px] text-amber-600 dark:text-amber-400">sin tarifa</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
